import { realClock, type Clock, type Speaker } from './types';

export interface SpeechItem {
  pageId: number;
  text: string;
  /**
   * Spoken before `text`, computed the moment speech starts rather than when the item was
   * queued: an item can wait a minute behind another narration, and "in about two minutes on
   * your right" must describe where the car is now.
   */
  prelude?: () => string | null;
  /** Current distance to the landmark; kept fresh via updateDistance() so closest-first stays true. */
  distance_m: number;
  estSeconds: number;
  voice?: string;
  /** Once we are moving away and farther than this, a queued item is dropped: we've passed it. */
  baseRadiusM?: number;
  /** A queued item farther than this is dropped regardless of direction. */
  maxDistanceM?: number;
  /**
   * 'filler' is an area rundown played in a quiet stretch: it waits behind every landmark and is
   * the first thing displaced, because a landmark is tied to a place the car is about to pass
   * and a rundown is not. 'feature' is a river, forest or wilderness the road has been running
   * beside: it waits behind landmarks too, but a landmark arriving does not drop it, since the
   * car is still beside the river when the landmark is done. Default 'landmark'.
   */
  kind?: 'landmark' | 'feature' | 'filler';
}

const isFiller = (q: SpeechItem): boolean => q.kind === 'filler';
/** Past this angle off the heading a landmark is behind the car (intro.ts uses the same). */
const BEHIND_DEG = 110;
/**
 * A queued item behind the car and farther than this (and outside its base radius) is dropped.
 * Passing a landmark a kilometre off to the side, the distance barely grows between fixes, so
 * the moving-away test alone let Brown's Ferry Park start 1.2 km behind the car (H27 drive).
 */
const PASSED_BEHIND_M = 1000;
const RANK = { landmark: 0, feature: 1, filler: 2 } as const;
/** Landmarks, then features, then filler; closest first within each. */
const byPriority = (a: SpeechItem, b: SpeechItem): number => RANK[a.kind ?? 'landmark'] - RANK[b.kind ?? 'landmark'] || a.distance_m - b.distance_m;

export type FinishReason = 'done' | 'stopped' | 'error' | 'watchdog';
export type DropReason = 'passed' | 'out_of_range' | 'displaced';

export interface SpeechQueueOptions {
  clock: Clock;
  /** Breathing room between narrations (ms). */
  minGapMs: number;
  /** Closest-first; anything beyond this is dropped (it stays eligible to re-trigger later). */
  maxQueue: number;
  rate?: number;
  beginDucking?: () => void | Promise<void>;
  endDucking?: () => void | Promise<void>;
  /** Fired when speech actually starts. This is where the app marks a landmark as played. */
  onStart?: (item: SpeechItem) => void | Promise<void>;
  onFinish?: (item: SpeechItem, reason: FinishReason) => void;
  /** A queued item was discarded before it spoke. It stays eligible to trigger again later. */
  onDrop?: (item: SpeechItem, reason: DropReason) => void;
}

const DEFAULTS: SpeechQueueOptions = { clock: realClock, minGapMs: 8000, maxQueue: 3 };

/**
 * Single-flight narration queue. expo-speech will happily start a second utterance on top of
 * the first on Android, and on iOS queues natively with no way to inspect it, so we own the
 * queue. A watchdog guards against the known trap where onDone never fires after a system
 * sound interrupts the utterance; without it the queue deadlocks for the rest of the drive.
 *
 * Timers alone are not enough on the phone: React Native only runs JS timers while its activity
 * is resumed, so with the screen off neither the gap timer nor the watchdog fires. The host calls
 * tick() on every location fix, and the queue checks both deadlines against the clock there.
 */
export class SpeechQueue {
  readonly opts: SpeechQueueOptions;
  queue: SpeechItem[] = [];
  speaking = false;
  currentPageId: number | null = null;
  lastEndedAt = Number.NEGATIVE_INFINITY;
  private draining = false;
  private gapTimer: unknown = null;
  private watchdog: unknown = null;
  private watchdogAt: number | null = null;
  private finishCurrent: ((reason: FinishReason) => void) | null = null;

  constructor(private speaker: Speaker, opts: Partial<SpeechQueueOptions> = {}) {
    this.opts = { ...DEFAULTS, ...opts };
  }

  enqueue(item: SpeechItem): void {
    if (this.currentPageId === item.pageId) return;
    // A landmark arriving means the quiet stretch is over; a queued rundown would only delay it.
    if (!isFiller(item)) for (const q of this.queue.filter(isFiller)) this.drop(q, 'displaced');
    if (this.queue.some((q) => q.pageId === item.pageId)) return;
    this.queue.push(item);
    this.queue.sort(byPriority);
    while (this.queue.length > this.opts.maxQueue) {
      const dropped = this.queue.pop()!;
      this.opts.onDrop?.(dropped, 'displaced');
    }
    void this.drain();
  }

  /**
   * Fresh distance from the latest fix. Re-sorts closest-first and discards anything we have
   * driven past: a script about somewhere two kilometres behind the car is worse than silence.
   */
  updateDistance(pageId: number, distanceM: number, relDeg: number | null = null): void {
    const item = this.queue.find((q) => q.pageId === pageId);
    if (!item) return;
    const movingAway = distanceM > item.distance_m + 20;
    item.distance_m = distanceM;
    if (item.maxDistanceM != null && distanceM > item.maxDistanceM) return this.drop(item, 'out_of_range');
    if (movingAway && item.baseRadiusM != null && distanceM > item.baseRadiusM) return this.drop(item, 'passed');
    const behind = relDeg != null && Math.abs(relDeg) > BEHIND_DEG;
    if (behind && distanceM > Math.max(PASSED_BEHIND_M, item.baseRadiusM ?? 0)) return this.drop(item, 'passed');
    this.queue.sort(byPriority);
  }

  /** Drop a queued (not yet speaking) item, e.g. after driving out of range. */
  remove(pageId: number, reason: DropReason = 'out_of_range'): void {
    const item = this.queue.find((q) => q.pageId === pageId);
    if (item) this.drop(item, reason);
  }

  private drop(item: SpeechItem, reason: DropReason): void {
    this.queue = this.queue.filter((q) => q !== item);
    this.opts.onDrop?.(item, reason);
  }

  /** User hit skip, or we want the next one now. Fires onStopped -> finish. */
  skip(): void {
    this.speaker.stop();
  }

  clear(): void {
    this.queue = [];
    this.speaker.stop();
  }

  /**
   * Heartbeat from the host (every location fix in the app). Fires an overdue watchdog and
   * starts the next item once the gap has passed, without relying on timers.
   */
  tick(): void {
    if (this.finishCurrent && this.watchdogAt != null && this.opts.clock.now() >= this.watchdogAt) this.fireWatchdog();
    void this.drain();
  }

  private fireWatchdog(): void {
    // Finish first so the onStopped that stop() may fire is ignored, then stop the engine.
    this.finishCurrent?.('watchdog');
    this.speaker.stop();
  }

  private scheduleDrain(ms: number): void {
    if (this.gapTimer != null) this.opts.clock.clearTimeout(this.gapTimer);
    this.gapTimer = this.opts.clock.setTimeout(() => {
      this.gapTimer = null;
      void this.drain();
    }, ms);
  }

  async drain(): Promise<void> {
    if (this.speaking || this.draining || !this.queue.length) return;
    this.draining = true;
    try {
      const sinceLast = this.opts.clock.now() - this.lastEndedAt;
      if (sinceLast < this.opts.minGapMs) {
        this.scheduleDrain(this.opts.minGapMs - sinceLast);
        return;
      }
      if (await this.speaker.isSpeaking()) {
        this.scheduleDrain(1000);
        return;
      }

      const item = this.queue.shift()!;
      this.speaking = true;
      this.currentPageId = item.pageId;

      let finished = false;
      const finish = (reason: FinishReason): void => {
        if (finished) return;
        finished = true;
        if (this.watchdog != null) this.opts.clock.clearTimeout(this.watchdog);
        this.watchdog = null;
        this.watchdogAt = null;
        this.finishCurrent = null;
        this.speaking = false;
        this.currentPageId = null;
        this.lastEndedAt = this.opts.clock.now();
        void this.opts.endDucking?.();
        this.opts.onFinish?.(item, reason);
        void this.drain();
      };

      const watchdogMs = item.estSeconds * 1500 + 5000;
      this.finishCurrent = finish;
      this.watchdogAt = this.opts.clock.now() + watchdogMs;
      this.watchdog = this.opts.clock.setTimeout(() => this.fireWatchdog(), watchdogMs);

      try {
        await this.opts.beginDucking?.();
        await this.opts.onStart?.(item);
        // The watchdog may have given up on this item while onStart was stalled; speaking it
        // now would talk over whatever the queue starts next.
        if (finished) return;
        const lead = item.prelude?.();
        const text = lead ? `${lead} ${item.text}` : item.text;
        this.speaker.speak(text, {
          voice: item.voice,
          rate: this.opts.rate,
          onDone: () => finish('done'),
          onStopped: () => finish('stopped'),
          onError: () => finish('error'),
        });
      } catch {
        finish('error');
      }
    } finally {
      this.draining = false;
    }
  }
}
